import { useEffect } from 'react';

// Keys that scroll the page; blocked while the lock is active.
const SCROLL_KEYS = new Set([
  ' ', 'Spacebar', 'PageUp', 'PageDown', 'End', 'Home',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
]);

/**
 * Prevents the page from scrolling while `locked` is true by intercepting the scroll input
 * (wheel, touch and keyboard) instead of toggling `overflow`. This keeps the scrollbar
 * visible and its width unchanged, so locking does not shift the layout.
 */
const useScrollLock = (locked: boolean) => {
  useEffect(() => {
    if (!locked || typeof window === 'undefined') {
      return undefined;
    }

    const preventDefault = (event: Event) => event.preventDefault();

    const preventScrollKeys = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isEditable = target?.isContentEditable
        || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);

      if (!isEditable && SCROLL_KEYS.has(event.key)) {
        event.preventDefault();
      }
    };

    window.addEventListener('wheel', preventDefault, { passive: false });
    window.addEventListener('touchmove', preventDefault, { passive: false });
    window.addEventListener('keydown', preventScrollKeys);

    return () => {
      window.removeEventListener('wheel', preventDefault);
      window.removeEventListener('touchmove', preventDefault);
      window.removeEventListener('keydown', preventScrollKeys);
    };
  }, [locked]);
};

export default useScrollLock;
